// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/tabs/SlaTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Form, Button, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import SimpleTime from 'components/SimpleComponents/SimpleTime';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'omnichannel';
const CATEGORY = 'sla';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SlaTab = ({ loading: parentLoading, setLoading: setParentLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = appService.getModulePreferences({ moduleId: MODULE_ID, category: CATEGORY });
    if (stored) {
      form.setFieldsValue({
        slaAttendance: loadSla(stored.attendance),
        slaResponse: loadSla(stored.response),
      });
    }
  }, [form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    setParentLoading?.(true);
    try {
      const values = form.getFieldsValue(true);
      const data = {
        attendance: buildSlaPayload(values.slaAttendance),
        response: buildSlaPayload(values.slaResponse),
      };
      await appService.updateModulePreferences({
        moduleId: MODULE_ID,
        category: CATEGORY,
        data,
      });
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch {
      appService.message({ flag: 'e', text: 'error_operation' });
    } finally {
      setSaving(false);
      setParentLoading?.(false);
    }
  }, [form, setParentLoading]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const slaTypes = [
    { value: 'elapsed', label: translateX('sla_elapsed'), tooltip: translateX('sla_elapsed_hint') },
    { value: 'business', label: translateX('sla_business'), tooltip: translateX('sla_business_hint') },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Form form={form} layout="vertical" className="mt-3">
      <Form.Item
        name="slaAttendance"
        label={translateX('sla_attendance')}
        tooltip={translateX('sla_attendance_hint')}
      >
        <SimpleTime
          types={slaTypes}
          defaultType="elapsed"
          units={['minutes', 'hours', 'days']}
          defaultUnit="minutes"
        />
      </Form.Item>

      <Form.Item
        name="slaResponse"
        label={translateX('sla_response')}
        tooltip={translateX('sla_response_hint')}
      >
        <SimpleTime
          types={slaTypes}
          defaultType="elapsed"
          units={['minutes', 'hours']}
          defaultUnit="minutes"
        />
      </Form.Item>

      <Divider style={{ margin: '16px 0' }} />

      <div style={{ textAlign: 'right' }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving || parentLoading}
          onClick={handleSave}
        >
          {translateX('save')}
        </Button>
      </div>
    </Form>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SlaTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Load SLA from stored parameters → SimpleTime value format
function loadSla(sla) {
  if (sla?.businessTime?.minutes > 0) {
    return { type: 'business', ...sla.businessTime };
  }
  if (sla?.elapsedTime?.minutes > 0) {
    return { type: 'elapsed', ...sla.elapsedTime };
  }
  return { type: 'elapsed', value: 0, unit: 'minutes', minutes: 0 };
}

// Convert SimpleTime value → backend format { elapsedTime, businessTime }
function buildSlaPayload(sla) {
  const emptyElapsed = { value: 0, unit: 'minutes', minutes: 0 };
  const emptyBusiness = { value: 0, unit: 'minutes', minutes: 0 };
  if (!sla || !sla.value) return { elapsedTime: emptyElapsed, businessTime: emptyBusiness };

  const { type, value, unit, minutes } = sla;
  const entry = { value: value ?? 0, unit: unit ?? 'minutes', minutes: minutes ?? 0 };

  return {
    elapsedTime: type === 'elapsed' ? entry : emptyElapsed,
    businessTime: type === 'business' ? entry : emptyBusiness,
  };
}
